/**
 * EvalResultsHero — Slice 1a hero component for the redesigned results page.
 *
 * Replaces the SVG ring (EvaluationHero) on the redesigned results page with:
 *   1. A Fraunces numeral score inside a soft ELLIPSE frame, with a thin SVG
 *      arc ring at the top-right (accent-tinted, ~30% opacity). Score format:
 *      large numeral followed by a smaller dim "/100" suffix baseline-aligned.
 *   2. A coaching headline in Fraunces. The headline uses a SPLIT rendering:
 *      - Lead clause (before an em-dash "—" or first sentence end): upright
 *        --results-text-primary.
 *      - Remainder / emphasis clause: ITALIC --results-text-dim.
 *      This reverses the old D4 "no italic" rule for the headline only —
 *      italic emphasis is now INTENTIONAL for the trailing clause
 *      (owner 2026-07-28 prototype approval). The score numeral REMAINS upright
 *      (D4 for the numeral is unchanged).
 *   3. Refined DOT-PILLS: each pill has a small colored dot + letter-spaced body.
 *      Mirror pill → purple dot (--results-dot-mirror).
 *      Tempo pill  → green  dot (--results-dot-tempo).
 *      Phase pill  → amber  dot (--results-dot-phase).
 *
 * TOKEN CONTRACT (RESULTS_THEME_CONVENTION.md):
 *   Every colour, radius, and font reference goes through a --results-* or
 *   --font-results-* variable.  No hex values, no raw font names, no Tailwind
 *   color utilities.  This component MUST be rendered inside EvalResultsPageShell
 *   for the tokens to resolve.
 *
 * D4 RULE (updated 2026-07-28 prototype approval):
 *   The score NUMERAL: font-style: normal (upright, unchanged from original D4).
 *   The coaching HEADLINE: the LEAD CLAUSE is upright; the EMPHASIS CLAUSE
 *   (after an em-dash "—" or sentence boundary) is font-style: italic in
 *   --results-text-dim. This is a deliberate opt-in for the editorial aesthetic —
 *   see the JSX comment below for the split logic. Tests that previously asserted
 *   "headline has no italic" are updated to reflect this intentional reversal.
 *
 * ROLE-GATING:
 *   This component is visible to ALL authenticated users. It must never expose
 *   coach-only technical numbers (FORM SCORE, EOM, raw BDS/JAS values).
 *   The pill set is deliberately coaching-language only.
 *
 * PILLS — chosen set and rationale:
 *   1. "Mirrored to your handedness" (when should_mirror=true):
 *      Surfaces the mirroring decision in plain language so the athlete
 *      understands why their left/right appears flipped.
 *   2. Tempo verdict (when speed assessment present with an aggregate verdict):
 *      Plain-language verdict: "On target tempo" / "Slower than template" /
 *      "Faster than template".
 *   3. Phase count ("N phases evaluated"):
 *      Descriptive, not a score. Tells the athlete how many distinct segments
 *      their movement was broken into.
 */

import React, { useEffect, useRef, useState } from 'react';
import { FingerPrintIcon } from '@heroicons/react/24/outline';
import type { SpeedAssessment } from '@/types/api';
import { stripScoreRestatement } from './stripScoreRestatement';

// ── Animated count-up hook ────────────────────────────────────────────────────────
// Animates the hero numeral from 0 → target once on mount with an ease-out cubic,
// so the overall score "settles" in (matches the prototype). Honors
// prefers-reduced-motion by snapping straight to the final value.

function useAnimatedScore(target: number, durationMs = 900): number {
  const [value, setValue] = useState(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const reduce =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduce || target <= 0) {
      setValue(target);
      return;
    }

    let start: number | null = null;
    const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

    const tick = (ts: number) => {
      if (start === null) start = ts;
      const elapsed = ts - start;
      const t = Math.min(1, elapsed / durationMs);
      setValue(Math.round(target * easeOutCubic(t)));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [target, durationMs]);

  return value;
}

// ── Types ───────────────────────────────────────────────────────────────────────

export interface EvalResultsHeroProps {
  /** Overall performance score (0-100). Rendered as a large Fraunces numeral. */
  overallScore: number;
  /**
   * One-line coaching headline. First sentence of overall_summary, or a
   * fallback string. Rendered in Fraunces with editorial italic for the
   * emphasis clause (after em-dash or sentence boundary).
   */
  coachingHeadline?: string;
  /**
   * Number of phases evaluated. Rendered as a pill when > 0.
   * Descriptive, not a numeric metric — appropriate for all users.
   */
  phaseCount?: number;
  /**
   * Name of the lowest-scoring phase (no numbers — athlete-safe). When present
   * it renders the prototype's third pill: "<Phase> is your focus phase" and
   * REPLACES the plain phase-count pill (which remains the fallback).
   */
  focusPhaseLabel?: string | null;
  /**
   * Whether the scoring pipeline mirrored the athlete's video to match the
   * template's lead side. When true the "Mirrored to your handedness" pill
   * appears for ALL users (athlete and coach alike).
   */
  shouldMirror?: boolean;
  /**
   * Speed assessment array from metadata.speed_assessment.
   * When present, a tempo pill is derived from the majority verdict of
   * evaluated phases (verdict !== 'not_evaluated').
   */
  speedAssessment?: SpeedAssessment[] | null;
}

// ── Tempo pill derivation ────────────────────────────────────────────────────────
// Derives a single coaching-language tempo label from the per-phase speed
// assessment array. Only evaluated phases (verdict !== 'not_evaluated') count.
// Majority verdict wins; ties resolve to "On target tempo".

function deriveTempoPillLabel(phases: SpeedAssessment[]): string | null {
  const evaluated = phases.filter((p) => p.verdict !== 'not_evaluated');
  if (evaluated.length === 0) return null;

  let onTarget = 0;
  let tooSlow = 0;
  let tooFast = 0;

  for (const p of evaluated) {
    if (p.verdict === 'on_target') onTarget++;
    else if (p.verdict === 'too_slow') tooSlow++;
    else if (p.verdict === 'too_fast') tooFast++;
  }

  const max = Math.max(onTarget, tooSlow, tooFast);
  if (max === 0) return null;

  // Ties resolve to on_target (most neutral / highest quality signal).
  if (onTarget >= max) return 'On target tempo';
  if (tooSlow > tooFast) return 'Slower than template';
  return 'Faster than template';
}

// ── Headline split helper ────────────────────────────────────────────────────────
// Splits a headline into [leadClause, emphasisClause | null].
//
// D4 update (2026-07-28 prototype approval): italic emphasis is intentional
// for the trailing clause after an em-dash "—" or the first sentence boundary.
// If no natural split exists, the whole headline is upright (graceful fallback).
//
// Priority:
//   1. Em-dash "—" anywhere in the string → split there.
//   2. First "." or "?" or "!" that is followed by a space or end-of-string → split after it.
//   3. No split found → return [full text, null].

function splitHeadline(headline: string): [string, string | null] {
  // 1. Em-dash split
  const emDashIdx = headline.indexOf('—');
  if (emDashIdx !== -1) {
    const lead = headline.slice(0, emDashIdx + 1).trim(); // include the dash in the lead
    const emphasis = headline.slice(emDashIdx + 1).trim();
    if (emphasis.length > 0) return [lead, emphasis];
  }

  // 2. Sentence-boundary split (. ? !) followed by space or end-of-string
  const sentenceMatch = headline.match(/^(.+?[.?!])(\s+.+)$/s);
  if (sentenceMatch) {
    const lead = sentenceMatch[1].trim();
    const emphasis = sentenceMatch[2].trim();
    if (emphasis.length > 0) return [lead, emphasis];
  }

  // 3. Graceful fallback — no split
  return [headline, null];
}

// ── Hero arc — exact-ellipse SVG ─────────────────────────────────────────────────
// Ring geometry: the ring div is inset(-22px,-30px) around the measured frame
// box (w×h) → ellipse box (w+60)×(h+44). The arc is a parametric segment ON
// that ellipse from A1 to A2 (degrees clockwise from 12 o'clock), so it keeps
// the ring's spacing everywhere and can never cross the numerals.
// A1=-52°: crest begins above the first digit. A2=+78°: tail ends beside the
// block at right, just above its vertical middle (prototype spacing).

// The faint frame ring sits at inset(-22px,-30px) around the score box. The arc
// is drawn on a SLIGHTLY LARGER ellipse (ring inset + ARC_EXTRA_*) so it floats
// just clear of the numerals — more breathing room above them and beside the
// /100 superscript (owner 2026-08-01: "a little more space between the arc and
// the numbers, especially the superscript 100"). Weighted more vertical since
// "further up" is the primary ask.
const RING_INSET_Y = 22;
const RING_INSET_X = 30;
const ARC_EXTRA_Y = 14;
const ARC_EXTRA_X = 9;
const ARC_INSET_Y = RING_INSET_Y + ARC_EXTRA_Y;
const ARC_INSET_X = RING_INSET_X + ARC_EXTRA_X;

function HeroArc({ w, h }: { w: number; h: number }) {
  const W = w + ARC_INSET_X * 2;
  const H = h + ARC_INSET_Y * 2;
  const cx = W / 2;
  const cy = H / 2;
  const rx = W / 2 - 0.75;
  const ry = H / 2 - 0.75;

  const pt = (deg: number) => {
    const t = (deg * Math.PI) / 180;
    return { x: cx + rx * Math.sin(t), y: cy - ry * Math.cos(t) };
  };
  const A1 = -52;
  const A2 = 78;
  const p1 = pt(A1);
  const p2 = pt(A2);

  return (
    <svg
      aria-hidden="true"
      data-testid="eval-results-hero-arc"
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      style={{
        position: 'absolute',
        top: -ARC_INSET_Y,
        left: -ARC_INSET_X,
        pointerEvents: 'none',
        overflow: 'visible',
        filter: 'drop-shadow(0 0 6px color-mix(in srgb, var(--results-hero-arc) 70%, transparent))',
      }}
    >
      <defs>
        {/* Taper: transparent tips, full strength through the crest. */}
        <linearGradient
          id="hero-arc-taper"
          gradientUnits="userSpaceOnUse"
          x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y}
        >
          <stop offset="0" stopColor="var(--results-hero-arc)" stopOpacity="0" />
          <stop offset="0.12" stopColor="var(--results-hero-arc)" stopOpacity="0.9" />
          <stop offset="0.75" stopColor="var(--results-hero-arc)" stopOpacity="0.9" />
          <stop offset="1" stopColor="var(--results-hero-arc)" stopOpacity="0" />
        </linearGradient>
      </defs>
      {/* pathLength=1 normalizes the dash space so the CSS draw-on keyframe
          (.results-hero-arc-draw, stroke-dashoffset 1→0) sweeps the arc from
          its start above the first digit down to the right tail, in step with
          the numeral count-up. Reduced-motion renders it fully drawn. */}
      <path
        className="results-hero-arc-draw"
        d={`M ${p1.x} ${p1.y} A ${rx} ${ry} 0 0 1 ${p2.x} ${p2.y}`}
        pathLength={1}
        fill="none"
        stroke="url(#hero-arc-taper)"
        strokeWidth={1.2}
      />
    </svg>
  );
}

// ── Dot-Pill component ───────────────────────────────────────────────────────────
// Refined pill design: colored dot + letter-spaced body text.
// Subtle border and faint raised bg for quietness.

interface DotPillProps {
  children: React.ReactNode;
  /** CSS token name for the dot color — e.g. '--results-dot-mirror'. */
  dotToken: string;
  /** data-testid for test targeting */
  'data-testid'?: string;
  icon?: React.ReactNode;
}

function DotPill({ children, dotToken, 'data-testid': testId, icon }: DotPillProps) {
  const dotColor = `var(${dotToken})`;
  return (
    <span
      data-testid={testId}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        padding: '5px 12px',
        borderRadius: 'var(--results-radius-pill)',
        border: `1px solid var(--results-surface-line)`,
        backgroundColor: 'var(--results-surface-raised-2)',
        color: 'var(--results-text-dim)',
        fontFamily: 'var(--font-results-body)',
        fontSize: '11px',
        fontWeight: 500,
        fontStyle: 'normal',
        letterSpacing: '0.04em',
        whiteSpace: 'nowrap',
      }}
    >
      {/* Colored dot */}
      <span
        aria-hidden="true"
        style={{
          display: 'inline-block',
          width: '6px',
          height: '6px',
          borderRadius: '50%',
          background: dotColor,
          flexShrink: 0,
        }}
      />
      {icon}
      {children}
    </span>
  );
}

// ── EvalResultsHero ──────────────────────────────────────────────────────────────

export default function EvalResultsHero({
  overallScore,
  coachingHeadline,
  phaseCount,
  focusPhaseLabel,
  shouldMirror,
  speedAssessment,
}: EvalResultsHeroProps): React.JSX.Element {
  // Clamp score to 0-100 for safety.
  const score = Math.max(0, Math.min(100, Math.round(overallScore)));

  // Measure the score frame box so HeroArc can be drawn exactly on the ring
  // ellipse (re-measures on resize; size is stable during the count-up thanks
  // to the minWidth `ch` reservation).
  const frameWrapRef = useRef<HTMLDivElement | null>(null);
  const [frameSize, setFrameSize] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = frameWrapRef.current;
    if (!el) return;
    const measure = () =>
      setFrameSize((prev) => {
        const w = el.offsetWidth;
        const h = el.offsetHeight;
        return prev.w === w && prev.h === h ? prev : { w, h };
      });
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      return () => ro.disconnect();
    }
    return undefined;
  }, []);

  // Animated count-up for the numeral (settles 0 → score on mount).
  const displayScore = useAnimatedScore(score);

  // Headline split — lead clause upright, emphasis clause italic.
  const rawHeadline = stripScoreRestatement(coachingHeadline?.trim() || '') || 'Evaluation complete';
  const [leadClause, emphasisClause] = splitHeadline(rawHeadline);

  // Pills — assembled in display order.
  const mirrorPill = shouldMirror === true;
  const tempoPillLabel =
    speedAssessment && speedAssessment.length > 0
      ? deriveTempoPillLabel(speedAssessment)
      : null;
  // Prototype pill #3: focus phase (name only). Falls back to the plain count.
  const phasePillLabel = focusPhaseLabel
    ? `${focusPhaseLabel} is your focus phase`
    : phaseCount !== undefined && phaseCount > 0
      ? `${phaseCount} phase${phaseCount === 1 ? '' : 's'} evaluated`
      : null;

  // Arc ring path. Rendered as SVG on top-right of the ellipse.
  // Describes a thin arc from ~310° to ~90° (top-right quadrant sweep).
  // The arc is purely decorative and aria-hidden.

  return (
    <section
      aria-label="Evaluation hero — overall score and verdict"
      data-testid="eval-results-hero"
      style={{
        /* Open editorial hero — NO card chrome (owner 2026-08-01: the filled,
           bordered box read as "blocky"). The score + verdict sit directly on
           the page canvas like the prototype. */
        padding: 'clamp(8px, 2vw, 20px) 0 clamp(20px, 3vw, 32px)',
        position: 'relative',
      }}
    >
      {/* Subtle editorial texture — faint brand-cyan glow at top-right (prototype
          hero bg: radial rgba(45,226,230,.055)) */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: '-10%',
          right: 0,
          width: '55%',
          height: '120%',
          background: `radial-gradient(ellipse at 75% 30%, color-mix(in srgb, var(--results-hero-arc) 7%, transparent), transparent 65%)`,
          pointerEvents: 'none',
        }}
      />

      <div className="eval-hero-grid" style={{ position: 'relative', zIndex: 1 }}>
        {/* ══ LEFT COLUMN — score block ═══════════════════════════════════ */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
        {/* ── Score numeral in elliptical frame ─────────────────────────── */}
        {/*
         * Layout: score numerals sit inside an inline ellipse frame.
         * The ellipse has a faint amber-tinted border + inner glow for depth.
         * An SVG arc ring (top-right quadrant) adds the prototype's arc detail.
         * D4 (numeral): font-style: normal (upright) — UNCHANGED.
         */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '16px' }}>
          {/* Ellipse frame container — ref measured so the arc SVG can be drawn
              exactly on the ring ellipse (see HeroArc below). */}
          <div
            ref={frameWrapRef}
            style={{
              position: 'relative',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* PROTOTYPE-EXACT score block (owner 2026-08-01 fidelity pass).
                .score-block/.score-ring recipe: the ring is a TIGHT ellipse,
                inset(-22px,-30px) around the WHOLE score flow — numerals AND
                the /100 superscript — 1px hairline at opacity .5. */}
            <div
              aria-hidden="true"
              style={{
                position: 'absolute',
                inset: '-22px -30px',
                borderRadius: '50%',
                border: `1px solid var(--results-hero-frame-border)`,
                opacity: 0.5,
                pointerEvents: 'none',
              }}
            />
            {/* Cyan arc — drawn EXACTLY ON the ring ellipse (owner 2026-08-01:
                the rotated-border trick swung a non-circular ellipse's limb
                INSIDE the box and across the figures; a rotated wide ellipse no
                longer coincides with the ring). The SVG arc is computed from
                the measured frame box on the ring's own radii, so by
                construction it can never cross the glyphs. Gradient-tapered
                ends keep the prototype's clean tapering look. STATIC. */}
            {frameSize.w > 0 && frameSize.h > 0 && (
              <HeroArc w={frameSize.w} h={frameSize.h} />
            )}

            {/* Score numeral — prototype .score: Fraunces 300, 148px, lh .85,
                ls -.04em, white→blue-grey gradient text. Mobile clamps down. */}
            <span
              data-testid="eval-results-hero-score"
              aria-label={`Overall score: ${score} out of 100`}
              style={{
                fontFamily: 'var(--font-results-display)',
                fontStyle: 'normal', /* D4: MUST be normal — score numeral is never italic */
                fontWeight: 300,
                fontSize: 'clamp(72px, 17vw, 148px)',
                lineHeight: 0.85,
                background:
                  'linear-gradient(160deg, var(--results-numeral-from) 30%, var(--results-numeral-to))',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
                WebkitTextFillColor: 'transparent',
                letterSpacing: '-0.04em',
                display: 'inline-block',
                fontVariationSettings: '"opsz" 72',
                fontVariantNumeric: 'tabular-nums',
                /* LAYOUT-SHIFT FIX (owner 2026-08-01): reserve the FINAL score's
                   width from frame 0 (tabular-nums → 1ch = one digit) so the
                   count-up can never reflow the hero. */
                minWidth: `${String(score).length}ch`,
                textAlign: 'center',
              }}
            >
              {displayScore}
            </span>

            {/* /100 — prototype: a SUPERSCRIPT inside the score flow (30px,
                ink-faint, upper-right of the numerals), NOT baseline-bottom. */}
            <sup
              aria-hidden="true"
              style={{
                fontFamily: 'var(--font-results-display)',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: 'clamp(18px, 3vw, 30px)',
                lineHeight: 1,
                color: 'var(--results-text-faint)',
                letterSpacing: 0,
                /* sup baseline shift ≈ prototype's rendered position: the label
                   sits in the upper half beside the numerals. */
                alignSelf: 'flex-start',
                marginTop: 'clamp(14px, 2.6vw, 26px)',
              }}
            >
              /100
            </sup>
          </div>
        </div>
        </div>
        {/* ══ end LEFT column ═════════════════════════════════════════════ */}

        {/* ══ RIGHT COLUMN — verdict (kicker + headline + pills) ══════════ */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 }}>
        {/* "Evaluation Verdict" kicker — cyan, letter-spaced (prototype .kick). */}
        <span
          data-testid="eval-results-hero-kicker"
          style={{
            fontFamily: 'var(--font-results-mono)',
            fontStyle: 'normal',
            fontSize: '10.5px',
            fontWeight: 500,
            letterSpacing: '0.34em',
            textTransform: 'uppercase',
            color: 'var(--results-hero-arc)',
            display: 'block',
          }}
        >
          Evaluation Verdict
        </span>

        {/* ── Coaching headline with italic emphasis clause ─────────────── */}
        {/*
         * D4 UPDATE (2026-07-28 owner prototype approval):
         *   The LEAD CLAUSE is upright Fraunces (calm, consistent).
         *   The EMPHASIS CLAUSE (after em-dash or sentence boundary) is
         *   ITALIC Fraunces in --results-text-dim — editorial emphasis.
         *   If there is no natural split, the whole headline is upright.
         *   The score NUMERAL remains upright (D4 for the numeral is unchanged).
         *
         * Tests that previously asserted "headline has no italic" are updated
         * to reflect this intentional reversal (see EvalResultsHero.test.tsx).
         */}
        <h2
          data-testid="eval-results-hero-headline"
          style={{
            fontFamily: 'var(--font-results-display)',
            fontStyle: 'normal', /* container is normal; spans override per-clause */
            fontWeight: 400,
            /* Prototype .verdict h1: 34px, lh 1.15, ls -.01em, measure 560px. */
            fontSize: 'clamp(20px, 3vw, 34px)',
            lineHeight: 1.15,
            color: 'var(--results-text-primary)',
            margin: 0,
            letterSpacing: '-0.01em',
            maxWidth: '560px',
            overflow: 'hidden',
            display: '-webkit-box',
            /* 2-line clamp: the hero verdict is a HEADLINE, not the essay —
               the full summary lives in the coaching section below. */
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
          }}
          title={rawHeadline}
        >
          {/* Lead clause — upright, primary color */}
          <span
            data-testid="eval-results-hero-headline-lead"
            style={{
              fontStyle: 'normal',
              color: 'var(--results-text-primary)',
            }}
          >
            {leadClause}
          </span>
          {/* Emphasis clause — italic, dim (only rendered when split found) */}
          {emphasisClause && (
            <>
              {' '}
              <span
                data-testid="eval-results-hero-headline-emphasis"
                style={{
                  fontStyle: 'italic',
                  color: 'var(--results-text-dim)',
                }}
              >
                {emphasisClause}
              </span>
            </>
          )}
        </h2>

        {/* ── Dot-pills row ─────────────────────────────────────────────── */}
        {/* All pills use coaching language and are visible to ALL users.
            Refined design: colored dot + letter-spaced label, subtle border. */}
        {(mirrorPill || tempoPillLabel || phasePillLabel) && (
          <div
            data-testid="eval-results-hero-pills"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              alignItems: 'center',
            }}
          >
            {/* Mirror pill — purple dot, appears when pipeline mirrored athlete video */}
            {mirrorPill && (
              <DotPill
                data-testid="eval-results-hero-pill-mirror"
                dotToken="--results-dot-mirror"
                icon={
                  <FingerPrintIcon
                    aria-hidden="true"
                    style={{ width: '11px', height: '11px', flexShrink: 0 }}
                  />
                }
              >
                Mirrored to your handedness
              </DotPill>
            )}

            {/* Tempo pill — green dot, plain-language verdict from speed assessment */}
            {tempoPillLabel && (
              <DotPill
                data-testid="eval-results-hero-pill-tempo"
                dotToken="--results-dot-tempo"
              >
                {tempoPillLabel}
              </DotPill>
            )}

            {/* Phase count pill — amber dot, descriptive, not a score */}
            {phasePillLabel && (
              <DotPill
                data-testid="eval-results-hero-pill-phases"
                dotToken="--results-dot-phase"
              >
                {phasePillLabel}
              </DotPill>
            )}
          </div>
        )}
        </div>
        {/* ══ end RIGHT column ════════════════════════════════════════════ */}
      </div>
    </section>
  );
}
